import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Breadcrumb } from "../Breadcrumb";
import { Save, X } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useNavigate } from "react-router-dom";
import { PATHS } from "../config/paths";

interface AddEditUserProps {
    mode?: "create" | "edit";
    user?: {
        id?: string;
        name: string;
        email: string;
        role: string;
        phone?: string;
        address?: string;
        // Optional specific fields
        course?: string;
        year_of_academy?: string;
        admin_role?: string;
        salary_package?: string;
    };
    currentUserRole?: string; // To check if the logged in user is Admin viewing a Teacher
}

export function EditProfile({ mode = "edit", user, currentUserRole = "Admin" }: AddEditUserProps) {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: user?.name || "",
        email: user?.email || "",
        phone: user?.phone || "+91 98765 43210",
        address: user?.address || "123 College Street, Education City, Mumbai 400001",
        role: user?.role || "Student",
        course: user?.course || "",
        year_of_academy: user?.year_of_academy || "",
        admin_role: user?.admin_role || "",
        salary_package: user?.salary_package || "",
    });

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        toast.success(mode === "create" ? "Profile created successfully!" : "Profile updated successfully!");
        setTimeout(() => {
            navigate(PATHS.VIEW_PROFILE);
        }, 1000);
    };

    return (
        <div className="min-h-screen bg-background p-6">
            <div className="max-w-4xl mx-auto space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white leading-none">
                        {mode === "create" ? "Create Profile" : "Edit Profile"}
                    </h1>


                    <Breadcrumb
                        items={[{ label: "Profile" }, { label: mode === "create" ? "Create Profile" : "Edit Profile" }]}
                        className="mt-2"
                    />
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                    <Card className="bg-card border-white/10 shadow-sm">
                        <CardHeader>
                            <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground/80">
                                {mode === "create" ? "Add New User" : "Personal Information"}
                            </CardTitle>

                            {mode === "create" && (
                                <p className="text-sm text-muted-foreground">Create a new user account</p>
                            )}
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {mode === "create" && (
                                    <div className="space-y-2 md:col-span-2">
                                        <Label htmlFor="role" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Role</Label>

                                        <Select
                                            value={formData.role}
                                            onValueChange={(value) => setFormData({ ...formData, role: value })}
                                        >
                                            <SelectTrigger id="role">
                                                <SelectValue placeholder="Select role" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Student">Student</SelectItem>
                                                <SelectItem value="Teacher">Teacher</SelectItem>
                                                <SelectItem value="Admin">Admin</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label htmlFor="name" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Full Name</Label>

                                    <Input
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Enter full name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Email Address</Label>

                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="Enter email"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="phone" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Phone Number</Label>

                                    <Input
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="Enter phone number"
                                    />
                                </div>

                                {/* Role Specific Fields */}
                                {formData.role === "Student" && (
                                    <>
                                        <div className="space-y-2">
                                            <Label htmlFor="course" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Course</Label>

                                            <Input
                                                id="course"
                                                name="course"
                                                value={formData.course}
                                                onChange={handleChange}
                                                placeholder="e.g. BCA, MCA"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="year_of_academy" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Year of Academy</Label>

                                            <Select
                                                value={formData.year_of_academy}
                                                onValueChange={(value) => setFormData({ ...formData, year_of_academy: value })}
                                            >
                                                <SelectTrigger id="year_of_academy">
                                                    <SelectValue placeholder="Select year" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="2024-2028">2024-2028</SelectItem>
                                                    <SelectItem value="2023-2027">2023-2027</SelectItem>
                                                    <SelectItem value="2022-2026">2022-2026</SelectItem>
                                                    <SelectItem value="2021-2025">2021-2025</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </>
                                )}

                                {formData.role === "Admin" && (
                                    <div className="space-y-2">
                                        <Label htmlFor="admin_role" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Admin Role</Label>

                                        <Select
                                            value={formData.admin_role}
                                            onValueChange={(value) => setFormData({ ...formData, admin_role: value })}
                                        >
                                            <SelectTrigger id="admin_role">
                                                <SelectValue placeholder="Select admin role" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Salary Manager">Salary Manager</SelectItem>
                                                <SelectItem value="Exam Controller">Exam Controller</SelectItem>
                                                <SelectItem value="Data Entry">Data Entry</SelectItem>
                                                <SelectItem value="Super Admin">Super Admin</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                {formData.role === "Teacher" && currentUserRole === "Admin" && (
                                    <div className="space-y-2">
                                        <Label htmlFor="salary_package" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Salary Package (Admin Only)</Label>

                                        <Input
                                            id="salary_package"
                                            name="salary_package"
                                            value={formData.salary_package}
                                            onChange={handleChange}
                                            placeholder="e.g. ₹8,00,000"
                                        />
                                    </div>
                                )}
                            </div>

                            {((formData.role === "Teacher" && currentUserRole === "Admin") || formData.role !== "Teacher") && (
                                <div className="space-y-2">
                                    <Label htmlFor="address" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">
                                        {formData.role === "Teacher" ? "Address (Admin Visible)" : "Address"}
                                    </Label>

                                    <Textarea
                                        id="address"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        placeholder="Enter address"
                                        rows={3}
                                    />
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Actions */}
                    <div className="flex gap-4 mt-6">
                        <Button type="submit" className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20 px-8 transition-all active:scale-95 h-10">
                            <Save className="h-4 w-4 mr-2" />
                            <span className="text-[13px] font-bold uppercase tracking-widest">Save Changes</span>
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            className="rounded-xl text-[13px] font-bold uppercase tracking-widest border border-red-500/10 h-10 px-8"
                            onClick={() => navigate(PATHS.VIEW_PROFILE)}
                        >
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                        </Button>
                    </div>


                </form>

                {/* Deprecated User Table Removed Here */}
            </div>
        </div>
    );
}
